import {Table} from '@/base/ui/table'
import {WuLoader} from '@/components/loader'
import {cn} from '@/lib/utils'
import {
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {cva} from 'class-variance-authority'
import React, {useEffect, useMemo} from 'react'
import {useRowSelection} from '../../hooks/useRowSelection'
import {IWuManualSorting} from '../../types/IWuManualSorting'
import {IWuTableColumnDef} from '../../types/IWuTableColumnDef'
import {WuTableBody} from '../../ui/_body'
import {WuTableHeader} from '../../ui/_header'

export interface IWuDataTableProps<T>
  extends React.TableHTMLAttributes<HTMLTableElement> {
  data: T[]
  columns: IWuTableColumnDef<T>[]
  pagination?: {
    pageIndex: number
    pageSize: number
  }
  variant?: 'unstyled' | 'striped' | 'bordered'
  size?: 'compact' | 'default'
  sorting?: {
    enabled: boolean
    onSort: (sort: IWuManualSorting) => void
  }
  isLoading?: boolean
  rowSelection?: {
    isEnabled: boolean
    selectedRows: T[]
    onRowSelect: React.Dispatch<React.SetStateAction<T[]>>
    rowUniqueKey: string
  }
  HeaderAction?: React.ReactElement
  NoDataContent?: React.ReactNode
}

export const WuDataTable = <T,>({
  data,
  columns,
  pagination,
  isLoading = false,
  variant = 'unstyled',
  sorting,
  size,
  rowSelection,
  HeaderAction,
  NoDataContent,
  ...props
}: IWuDataTableProps<T>): React.JSX.Element => {
  const [currentPage, setCurrentPage] = React.useState(
    pagination?.pageIndex ?? 0,
  )
  const selectedRowCount = rowSelection?.selectedRows?.length ?? 0

  const {finalColumns} = useRowSelection({
    columns,
    rowSelection,
  })

  const sortableColumns = useMemo(
    () => columns.filter(col => col.enableSorting).map(col => col.accessorKey),
    [finalColumns],
  )

  const table = useReactTable({
    data,
    columns: finalColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    enableRowSelection: rowSelection?.isEnabled,
  })

  useEffect(() => {
    if (pagination && pagination?.pageIndex !== currentPage) {
      rowSelection?.onRowSelect(() => [])
      table.resetRowSelection()
      setCurrentPage(pagination.pageIndex)
    }
  }, [pagination?.pageIndex])

  return (
    <div className={cn('wu-table-container')}>
      <Table
        {...props}
        className={cn(variantClasses({variant}), props.className)}
      >
        <WuTableHeader
          table={table}
          sortableColumns={sortableColumns}
          manual={!!sorting?.enabled}
          onSort={sorting?.onSort}
          size={size}
          HeaderAction={HeaderAction}
          isRowSelected={selectedRowCount > 0}
        />
        {!isLoading && (
          <WuTableBody
            table={table}
            size={size}
            NoDataContent={NoDataContent}
          />
        )}
      </Table>

      {isLoading && (
        <div className="flex items-center justify-center h-96">
          <WuLoader />
        </div>
      )}
    </div>
  )
}
const variantClasses = cva('', {
  variants: {
    variant: {
      striped: '[&_tr:nth-child(even)]:bg-zinc-50',
      unstyled: '',
      bordered: 'border-collapse border [&_th]:border [&_td]:border',
    },
  },
})
